/**
 * What each kind of flag is called, how it is drawn, and what it means in the owner's words.
 *
 * Lifted out of the dashboard on 16 September 2026 when the flags moved to their own page, so the
 * one table serves both the page and anything else that names a flag.
 */
import {
  KeyRound,
  MapPinOff,
  Moon,
  SatelliteDish,
  ShieldAlert,
  Smartphone,
  type LucideIcon,
} from 'lucide-react';
import type { Tone } from '../lib/components.js';

export type Flag = {
  id: string;
  kind: string;
  worker: { id: string; name: string } | null;
  created_at: string;
  /** The work day this flag belongs to, so a row can open that day for that worker. */
  work_date: string;
  detail: string;
};

export /**
 * What each kind is, in the owner's words, and what happened to the punch — which is the first
 * question anybody asks of a list like this and the one the panel used not to answer at all.
 */
const KIND: Record<string, { label: string; icon: LucideIcon; tone: Tone; means: string }> = {
  outside_geofence: {
    label: 'Outside geofence',
    icon: MapPinOff,
    tone: 'red',
    means: 'Somebody tried to punch from outside the office area. The punch was refused.',
  },
  weak_gps: {
    label: 'Weak GPS',
    icon: SatelliteDish,
    tone: 'amber',
    means: 'The phone gave a position it was not sure of — usually a roof, a wall or a bad signal. The punch was recorded.',
  },
  location_unverified: {
    label: 'Location unverified',
    icon: SatelliteDish,
    tone: 'amber',
    means: 'The location could not be checked against the office for this punch. The punch was recorded.',
  },
  location_unverified_pattern: {
    label: 'Phone keeps failing to locate',
    icon: Smartphone,
    tone: 'violet',
    means: 'One phone keeps failing to give a location. Worth checking its location settings once.',
  },
  credential_reuse: {
    label: 'Credential conflict',
    icon: KeyRound,
    tone: 'violet',
    means: 'A fingerprint that already belongs to another account was offered on this one.',
  },
  revoked_credential_auth: {
    label: 'Revoked credential use',
    icon: ShieldAlert,
    tone: 'red',
    means: 'Somebody tried to sign in with a passkey that had been cancelled.',
  },
  no_punchout: {
    label: 'No punch-out',
    icon: Moon,
    tone: 'blue',
    means: 'The shift was still open at the cutoff, so it was closed automatically.',
  },
  /**
   * The worker was told only that the punch could not be verified — on purpose, so that
   * nothing tells them which check fired or what to change. Here it says exactly what happened:
   * the difference between somebody cheating and somebody who left a location-simulation app
   * switched on by accident is a conversation, and the owner needs the facts to have it.
   */
  mock_location: {
    label: 'Fake GPS app detected',
    icon: MapPinOff,
    tone: 'red',
    means: 'The phone said its position came from a location-simulation app. The punch was refused.',
  },
  integrity_failed: {
    label: 'Failed device integrity',
    icon: ShieldAlert,
    tone: 'red',
    means: 'The app could not prove it was our app on a phone nobody had tampered with. The punch was refused.',
  },
  app_required: {
    label: 'Browser punch refused',
    icon: Smartphone,
    tone: 'amber',
    means: 'They have the app, so a browser punch is refused. Check they punched again from it.',
  },
};
